import type {
  EnvironmentsAttributeDetailResponse,
  EnvironmentsAttributeListItem,
  EnvironmentsAttributeUsageCount,
  EnvironmentsEventDetailResponse,
  EnvironmentsEventUsageCount,
  EnvironmentsListItem,
  EnvironmentsObjectAttributeUsageCount,
  EnvironmentsSchemaAttributeFiltersResponse,
  EnvironmentsSchemaEventFiltersResponse,
  EnvironmentsTotals,
  Tag,
  operations,
} from '@customerio/hydra-ui-api';

/**
 * The data index's reads and writes: the schema browser over a workspace's
 * profile attributes, object attributes, relationship attributes and events.
 *
 * Distinct from `services/db/attribute`, which serves attribute *names* to
 * pickers. This slice serves the rows behind those names — usage, sources,
 * tags, descriptions and privacy — and the writes that edit them.
 *
 * These are searched, filtered, paged reads of data written by ingestion
 * rather than by this app, so none of them is ever served from cache alone
 * (`staleTime: 0`). Keys are hand-rolled under the workspace subtree, because
 * a row's identity is its name plus the scope it was read in, not an id.
 */

/* ------------------------------------------------------------------------- */
/* Scope                                                                      */
/* ------------------------------------------------------------------------- */

/**
 * Which family of attributes a read addresses. Object and relationship
 * attributes live under a specific object type and are served by their own
 * endpoints, so the object type id is part of the scope rather than an
 * optional argument that three of the four call sites must remember.
 */
export type AttributeScope =
  | { kind: 'profile' }
  | { kind: 'object'; objectTypeId: number }
  | { kind: 'relationship'; objectTypeId: number };

/** A scope as a cache-key segment. */
function scopeKey(scope: AttributeScope): string {
  return scope.kind === 'profile' ? 'profile' : `${scope.kind}:${String(scope.objectTypeId)}`;
}

/* ------------------------------------------------------------------------- */
/* Wire types                                                                 */
/* ------------------------------------------------------------------------- */

export type DataIndexAttributeRow = EnvironmentsAttributeListItem;
export type DataIndexEventRow = EnvironmentsListItem;
export type DataIndexAttribute = EnvironmentsAttributeDetailResponse;
export type DataIndexEvent = EnvironmentsEventDetailResponse;
export type DataIndexTotals = EnvironmentsTotals;
export type DataIndexAttributeFilters = EnvironmentsSchemaAttributeFiltersResponse;
export type DataIndexEventFilters = EnvironmentsSchemaEventFiltersResponse;
export type DataIndexAttributeUsage = EnvironmentsAttributeUsageCount;
export type DataIndexObjectAttributeUsage = EnvironmentsObjectAttributeUsageCount;
export type DataIndexEventUsage = EnvironmentsEventUsageCount;

/** A page of attribute rows, with the tags the rows reference. */
export type DataIndexAttributePage = {
  attributes: DataIndexAttributeRow[];
  tags: Tag[];
};

/** A page of event rows, with the tags the rows reference. */
export type DataIndexEventPage = {
  events: DataIndexEventRow[];
  tags: Tag[];
};

/* ------------------------------------------------------------------------- */
/* List parameters                                                            */
/* ------------------------------------------------------------------------- */

type AttributesQuery = NonNullable<operations['get-v1-environments-environment_id-attributes']['parameters']['query']>;

type EventsQuery = NonNullable<operations['get-v1-environments-environment_id-event_names']['parameters']['query']>;

type AttributeTotalsQuery = NonNullable<
  operations['get-v1-environments-environment_id-attributes_totals']['parameters']['query']
>;

type EventTotalsQuery = NonNullable<
  operations['get-v1-environments-environment_id-event_names_totals']['parameters']['query']
>;

/**
 * What decides which rows are in scope, in the wire's own spelling. Shared by
 * the attribute and event families, which accept the same filters.
 */
export type DataIndexNarrowing = Pick<EventsQuery, 'search' | 'inUseOnly' | 'searchTagIds[]' | 'sourceIds[]'>;

/** A narrowing plus the page of it to fetch. */
export type DataIndexListFilters = DataIndexNarrowing & Pick<EventsQuery, 'page'>;

/**
 * The narrowing a set of list filters describes, with the page dropped.
 *
 * Totals count against a narrowing without paging it, so passing the list's
 * own filters would both send a param the endpoint ignores and give every
 * page its own totals slot.
 */
export function toDataIndexNarrowing(filters: DataIndexListFilters): DataIndexNarrowing {
  const { page: _page, ...narrowing } = filters;
  return narrowing;
}

/** The largest page the endpoints serve; asking for more is capped server-side. */
export const DATA_INDEX_PAGE_SIZE = 50;

/**
 * The scope's own parameters. A profile read sends neither, matching what the
 * page it replaces sent: an omitted param and a false one are the same answer
 * to the server but different cache keys here.
 */
function scopeQuery(scope: AttributeScope): Pick<AttributesQuery, 'objectType' | 'relationship'> {
  if (scope.kind === 'profile') {
    return {};
  }
  if (scope.kind === 'relationship') {
    return { objectType: scope.objectTypeId, relationship: true };
  }
  return { objectType: scope.objectTypeId };
}

export function buildAttributesQuery(filters: DataIndexListFilters, scope: AttributeScope): AttributesQuery {
  return { ...filters, ...scopeQuery(scope), size: DATA_INDEX_PAGE_SIZE };
}

export function buildAttributeTotalsQuery(narrowing: DataIndexNarrowing, scope: AttributeScope): AttributeTotalsQuery {
  return { ...narrowing, ...scopeQuery(scope) };
}

export function buildEventsQuery(filters: DataIndexListFilters): EventsQuery {
  return { ...filters, size: DATA_INDEX_PAGE_SIZE };
}

export function buildEventTotalsQuery(narrowing: DataIndexNarrowing): EventTotalsQuery {
  return narrowing;
}

/* ------------------------------------------------------------------------- */
/* Keys                                                                       */
/* ------------------------------------------------------------------------- */

export const dataIndexKeys = {
  all: (workspaceId: number) => ['workspace', workspaceId, 'data-index'] as const,

  attributes: (workspaceId: number) => ['workspace', workspaceId, 'data-index', 'attributes'] as const,
  attributeLists: (workspaceId: number) => ['workspace', workspaceId, 'data-index', 'attributes', 'list'] as const,
  attributeTotalsAll: (workspaceId: number) =>
    ['workspace', workspaceId, 'data-index', 'attributes', 'totals'] as const,
  attributeUsageAll: (workspaceId: number) => ['workspace', workspaceId, 'data-index', 'attributes', 'usage'] as const,
  attributeList: (workspaceId: number, scope: AttributeScope, filters: DataIndexListFilters) =>
    ['workspace', workspaceId, 'data-index', 'attributes', 'list', scopeKey(scope), filters] as const,
  attributeDetail: (workspaceId: number, scope: AttributeScope, name: string) =>
    ['workspace', workspaceId, 'data-index', 'attributes', 'detail', scopeKey(scope), name] as const,
  attributeTotals: (workspaceId: number, scope: AttributeScope, narrowing: DataIndexNarrowing) =>
    ['workspace', workspaceId, 'data-index', 'attributes', 'totals', scopeKey(scope), narrowing] as const,
  attributeFilters: (workspaceId: number, scope: AttributeScope) =>
    ['workspace', workspaceId, 'data-index', 'attributes', 'filters', scopeKey(scope)] as const,
  attributeUsage: (workspaceId: number, scope: AttributeScope, names: readonly string[]) =>
    ['workspace', workspaceId, 'data-index', 'attributes', 'usage', scopeKey(scope), names] as const,
  attributeProfileCounts: (workspaceId: number, names: readonly string[]) =>
    ['workspace', workspaceId, 'data-index', 'attributes', 'profile-counts', names] as const,
  attributeObjectCounts: (workspaceId: number, objectTypeId: number) =>
    ['workspace', workspaceId, 'data-index', 'attributes', 'object-counts', objectTypeId] as const,

  events: (workspaceId: number) => ['workspace', workspaceId, 'data-index', 'events'] as const,
  eventLists: (workspaceId: number) => ['workspace', workspaceId, 'data-index', 'events', 'list'] as const,
  eventTotalsAll: (workspaceId: number) => ['workspace', workspaceId, 'data-index', 'events', 'totals'] as const,
  eventUsageAll: (workspaceId: number) => ['workspace', workspaceId, 'data-index', 'events', 'usage'] as const,
  eventList: (workspaceId: number, filters: DataIndexListFilters) =>
    ['workspace', workspaceId, 'data-index', 'events', 'list', filters] as const,
  eventDetail: (workspaceId: number, name: string) =>
    ['workspace', workspaceId, 'data-index', 'events', 'detail', name] as const,
  eventTotals: (workspaceId: number, narrowing: DataIndexNarrowing) =>
    ['workspace', workspaceId, 'data-index', 'events', 'totals', narrowing] as const,
  eventFilters: (workspaceId: number) => ['workspace', workspaceId, 'data-index', 'events', 'filters'] as const,
  eventUsage: (workspaceId: number, names: readonly string[]) =>
    ['workspace', workspaceId, 'data-index', 'events', 'usage', names] as const,
  eventProfileCounts: (workspaceId: number, names: readonly string[]) =>
    ['workspace', workspaceId, 'data-index', 'events', 'profile-counts', names] as const,
};
